import { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { CameraOff, Keyboard } from 'lucide-react';

export default function BarcodeScanner({ open, onClose, onDetected, title = 'Scan Barcode' }) {
  const [error, setError] = useState('');
  const [manual, setManual] = useState('');
  const stateRef = useRef({ scanner: null, active: false });

  useEffect(() => {
    if (!open) return;
    setError('');
    setManual('');
    const state = stateRef.current;
    state.active = true;

    const stopScanner = () => {
      const s = state.scanner;
      if (!s) return Promise.resolve();
      try {
        const st = s.getState();
        if (st === 2 || st === 3) return s.stop().catch(() => {});
      } catch { /* scanner belum berjalan */ }
      return Promise.resolve();
    };

    const timer = setTimeout(() => {
      if (!state.active) return;
      const el = document.getElementById('barcode-reader');
      if (!el) return;
      const scanner = new Html5Qrcode('barcode-reader', false);
      state.scanner = scanner;
      scanner
        .start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 220, height: 160 } },
          (decoded) => {
            if (!state.active) return;
            state.active = false;
            stopScanner().finally(() => {
              onDetected(decoded);
              onClose();
            });
          },
          () => {}
        )
        .catch(() => {
          if (state.active) setError('Kamera tidak bisa diakses. Izinkan akses kamera di browser, atau ketik kode barcode manual di bawah.');
        });
    }, 300);

    return () => {
      clearTimeout(timer);
      state.active = false;
      stopScanner().then(() => {
        try { state.scanner?.clear(); } catch { /* abaikan */ }
        state.scanner = null;
      });
    };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const submitManual = () => {
    const code = manual.trim();
    if (!code) return;
    stateRef.current.active = false;
    onDetected(code);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-surface border-line max-w-sm" data-testid="barcode-scanner-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{title}</DialogTitle>
        </DialogHeader>
        <div className="relative">
          <div id="barcode-reader" className="w-full rounded-lg overflow-hidden bg-black min-h-[240px]" data-testid="barcode-reader" />
          {error && (
            <div className="absolute inset-0 bg-surface/95 rounded-lg flex flex-col items-center justify-center gap-2 p-4 text-center" data-testid="scanner-error">
              <CameraOff className="w-10 h-10 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">{error}</p>
            </div>
          )}
        </div>
        <div className="flex gap-2 mt-3">
          <input value={manual} onChange={(e) => setManual(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && submitManual()}
            placeholder="Atau ketik kode barcode..." className="lodi-input" data-testid="scanner-manual-input" />
          <button onClick={submitManual} data-testid="scanner-manual-submit"
            className="shrink-0 px-4 rounded-lg bg-brand hover:bg-branddark text-white text-sm font-bold flex items-center gap-1.5 transition-colors active:scale-[0.98]">
            <Keyboard className="w-4 h-4" /> Pakai
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
